import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { identity, isApiError, tickets } from '@repletoryreach/sdk-api';
import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';
import type { Translate } from '../../../i18n/I18nProvider';
import { byStatusOrder } from './status';

export { byStatusOrder, statusesForType } from './status';

/** Every tickets query starts with this key, so one invalidation refreshes the lot. */
export const TICKETS_KEY = 'tickets';

export function useTicketSettings() {
  return useQuery({ queryKey: [TICKETS_KEY, 'settings'], queryFn: () => tickets.getSettings(), staleTime: 60_000 });
}

export function useTicketTypes() {
  return useQuery({ queryKey: [TICKETS_KEY, 'types'], queryFn: () => tickets.listTypes(), staleTime: 60_000 });
}

export function useTicketStatuses() {
  return useQuery({ queryKey: [TICKETS_KEY, 'statuses'], queryFn: () => tickets.listStatuses(), staleTime: 60_000 });
}

export function useTicketPriorities() {
  return useQuery({ queryKey: [TICKETS_KEY, 'priorities'], queryFn: () => tickets.listPriorities(), staleTime: 60_000 });
}

/** The organisation's teams (identity `GET /v1/teams`); the same key the team settings use. */
export function useTeamsList() {
  return useQuery({ queryKey: ['teams'], queryFn: () => identity.listTeams(), staleTime: 60_000, retry: false });
}

/** Everyone in the organisation (identity `GET /v1/users`); the same key the team settings use. */
export function usePeople() {
  return useQuery({ queryKey: ['team', 'all-people'], queryFn: () => identity.listUsers({ limit: 500 }), staleTime: 60_000, retry: false });
}

export function personName(p: identity.User | undefined): string {
  return p ? (p.display_name?.trim() || p.email || '') : '';
}

export interface Lookups {
  types: tickets.TicketType[];
  statuses: tickets.Status[];
  priorities: tickets.Priority[];
  teams: identity.Team[];
  people: identity.User[];
  typeById: Map<string, tickets.TicketType>;
  statusById: Map<string, tickets.Status>;
  priorityById: Map<string, tickets.Priority>;
  teamName: (id: string | null | undefined) => string;
  personLabel: (id: string | null | undefined) => string;
  loading: boolean;
}

/** Names for every id a ticket carries: types, statuses, priorities, teams and people. */
export function useLookups(): Lookups {
  const typesQ = useTicketTypes();
  const statusesQ = useTicketStatuses();
  const prioritiesQ = useTicketPriorities();
  const teamsQ = useTeamsList();
  const peopleQ = usePeople();
  return useMemo(() => {
    const types = typesQ.data?.data ?? [];
    const statuses = [...(statusesQ.data?.data ?? [])].sort(byStatusOrder);
    const priorities = [...(prioritiesQ.data?.data ?? [])].sort((a, b) => a.rank - b.rank);
    const teams = [...(teamsQ.data?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const people = [...((peopleQ.data?.data ?? []) as identity.User[])].sort((a, b) => personName(a).localeCompare(personName(b)));
    const teamById = new Map(teams.map((x) => [x.id, x]));
    const personById = new Map(people.map((x) => [x.id, x]));
    return {
      types,
      statuses,
      priorities,
      teams,
      people,
      typeById: new Map(types.map((x) => [x.id, x])),
      statusById: new Map(statuses.map((x) => [x.id, x])),
      priorityById: new Map(priorities.map((x) => [x.id, x])),
      teamName: (id) => (id ? teamById.get(id)?.name ?? '' : ''),
      personLabel: (id) => (id ? personName(personById.get(id)) : ''),
      loading: typesQ.isPending || statusesQ.isPending || prioritiesQ.isPending
    };
  }, [typesQ.data, statusesQ.data, prioritiesQ.data, teamsQ.data, peopleQ.data, typesQ.isPending, statusesQ.isPending, prioritiesQ.isPending]);
}

/** What went wrong, in the customer's words. */
export function ticketErrorText(t: Translate, e: unknown): string {
  const code = tickets.ticketErrorCode(e);
  if (code) return t(`tickets.error.${code}`);
  if (isApiError(e) && e.status === 403) return t('tickets.error.forbidden');
  if (isApiError(e) && e.status === 404) return t('tickets.error.notFound');
  if (isApiError(e) && e.status === 503) return t('tickets.error.unavailable');
  return e instanceof Error && e.message ? e.message : t('common.unknownError');
}

export function categoryTone(category: string): StatusTone {
  switch (category) {
    case 'new': return 'pending';
    case 'open': return 'warning';
    case 'pending': return 'degraded';
    case 'on_hold': return 'inactive';
    case 'solved': return 'healthy';
    case 'closed': return 'inactive';
    default: return 'inactive';
  }
}

export function categoryLabel(t: Translate, category: string): string {
  return t(`tickets.category.${category}`, category);
}

/** A stored colour when it is one, or the brand colour. */
export function swatchColour(colour: string | null | undefined): string {
  return colour && /^#[0-9a-fA-F]{3,8}$/.test(colour) ? colour : 'var(--rr-color-primary)';
}

/** "45 min", "3 h 05 min", "2 d 4 h": an average or a target in minutes. */
export function formatMinutes(t: Translate, minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return t('common.none');
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return t('tickets.duration.minutes', { m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('tickets.duration.hours', { h, m: String(m % 60).padStart(2, '0') });
  return t('tickets.duration.days', { d: Math.floor(h / 24), h: h % 24 });
}

/** A channel's name: WhatsApp, Email, Website chat… */
export function channelLabel(t: Translate, channel: string | null | undefined): string {
  return channel ? t(`tickets.channel.${channel}`, channel) : t('tickets.channel.console');
}
